import { SortOrder } from "@common/constants";
import {
    ActorType,
    AuditChannel,
    AuditLogChangeType,
    AuditLogScopeType,
    ResourceType,
} from "@common/types/auditLog";
import { z } from "zod";
import { initContract } from "@ts-rest/core";
import {
    ActivityProcessName,
    APIProcessName,
    RPCProcessName,
    SQSProcessName,
    TestProcessName,
} from "@common/constants/process-names";
import { HistoryGroupBy } from "@common/constants/history-source-types";
import { NO_HISTORY_CHANNEL } from "@common/constants/agent-channel";
import { scheduleActivityQualifiersSchema } from "@common/schemas/domains/accounting/scheduleActivity.schema";
import {
    ReportSettingsUpdateHistoryPresentationSchema,
    ReportTemplateChangeHistoryPresentationSchema,
} from "@common/schemas/domains/reportSettings/report-settings-activity.schema";
import { isoDateSchema, utcDatetime } from "@common/helpers/date";
import { zQueryNullableUuid } from "@common/schemas/query-nullable-uuid.schema";
import { ActivityOutcomeKind } from "@common/schemas/domains/activity/activity-outcomes.schema";
import { ActivityTimeZoneSchema } from "@common/schemas/domains/activity/activity-time-zone.schema";
import {
    ActivitySeries,
    HistoryCountsBreakdown,
    HistoryCountsBucket,
    historyCountsBucketKeySchema,
    refineHistoryCountsRange,
} from "@common/schemas/domains/activity/activity-timeline.schema";

export {
    ReportSettingsUpdateHistoryPresentationSchema,
    ReportTemplateChangeHistoryPresentationSchema,
};

const c = initContract();

export const RefValueSchema = z.object({
    value: z.any(),
    valueLabel: z.string().optional(),
});

export const AuditLogDiffSchema = z.object({
    field: z.string(),
    before: RefValueSchema,
    after: RefValueSchema,
});

export const AuditLogSourceTypeSchema = z.union([
    z.nativeEnum(SQSProcessName),
    z.nativeEnum(APIProcessName),
    z.nativeEnum(ActivityProcessName),
    z.nativeEnum(RPCProcessName),
    z.nativeEnum(TestProcessName),
]);

export const SourcePredicateSchema = z.object({
    sourceTypes: z.array(z.string()).min(1),
    resourceTypes: z.array(z.nativeEnum(ResourceType)).optional(),
    fieldsAllOf: z.array(z.string()).optional(),
    fieldsSubsetOf: z.array(z.string()).optional(),
    excludeFields: z.array(z.string()).optional(),
    changeTypes: z.array(z.nativeEnum(AuditLogChangeType)).optional(),
    systemsAnyOf: z.array(z.string()).optional(),
    systemsNoneOf: z.array(z.string()).optional(),
});

export const ActivityFilterSchema = z.object({
    resourceTypes: z.array(z.nativeEnum(ResourceType)).min(1),
    fields: z.array(z.string()).optional(),
});

export const AuditLogSchema = z.object({
    id: z.string(),
    scopeId: z.string(),
    scopeType: z.nativeEnum(AuditLogScopeType),
    entityId: z.number().nullable(),
    resourceType: z.nativeEnum(ResourceType),
    resourceId: z.string(),
    changeType: z.nativeEnum(AuditLogChangeType),
    actorId: z.number().nullable(),
    actorName: z.string().nullable(),
    actorType: z.nativeEnum(ActorType),
    channel: z.nativeEnum(AuditChannel).nullable().optional(),
    diff: z.array(AuditLogDiffSchema),
    sourceType: z.string(),
    sourceId: z.string().nullable(),
    createdAt: utcDatetime,
    metadata: z.record(z.string(), z.any()).nullable(),
    revertedAt: utcDatetime.nullable().optional(),
    // 32-hex W3C trace ID; null when no trace span is active.
    traceId: z.string().nullable(),
});

// Behavior-preserving base object exposed for agent-layer reuse (MCP overrides
// retype only sourceType/actorIds and re-apply the refinement below). Do not
// change field behavior here — importer API relies on AuditLogFilterSchema.
export const AuditLogFilterObjectSchema = z.object({
    resourceType: z.nativeEnum(ResourceType).optional(),
    resourceTypes: z.array(z.nativeEnum(ResourceType)).optional(),
    resourceId: z.string().optional(),
    changeType: z.nativeEnum(AuditLogChangeType).optional(),
    changeTypes: z.array(z.nativeEnum(AuditLogChangeType)).optional(),
    sourceType: AuditLogSourceTypeSchema.optional(),
    sourceId: z.string().optional(),
    actorId: z.number().optional(),
    actorType: z.nativeEnum(ActorType).optional(),
    minCreatedAt: z.coerce.date().optional(),
    maxCreatedAt: z.coerce.date().optional(),
    search: z.string().optional(),
    fields: z.array(z.string()).optional(),
    entityIds: z.array(z.coerce.number()).optional(),
    actorIds: z
        .array(
            z.union([
                z.literal(null),
                z.literal("").transform(() => null),
                z.coerce.number(),
            ]),
        )
        .optional(),
    sourceTypes: z.array(z.string()).optional(),
    sourcePredicates: z.array(SourcePredicateSchema).optional(),
    activityFilters: z.array(ActivityFilterSchema).optional(),
    excludeResourceTypes: z.array(z.nativeEnum(ResourceType)).optional(),
    showReverted: z.coerce.boolean().optional(),
    revertedOnly: z.coerce.boolean().optional(),
});

export const AuditLogFilterSchema = AuditLogFilterObjectSchema.refine(
    (data) => !data.showReverted || !data.revertedOnly,
    {
        message: "showReverted and revertedOnly are mutually exclusive",
    },
);

// History-specific filter: the raw AuditLog filter plus the "Updated through"
// channels list. Kept separate from AuditLogFilterSchema so the raw getAuditLogs
// endpoint, importer API and MCP activity_logs_list tool are unaffected.
export const HistoryFilterObjectSchema = AuditLogFilterObjectSchema.extend({
    channels: z.array(z.nativeEnum(AuditChannel)).optional(),
    outcome: z.nativeEnum(ActivityOutcomeKind).optional(),
    series: z.array(z.nativeEnum(ActivitySeries)).optional(),
});

export const HistoryFilterSchema = HistoryFilterObjectSchema.refine(
    (data) => !data.showReverted || !data.revertedOnly,
    {
        message: "showReverted and revertedOnly are mutually exclusive",
    },
);

// Sortable AuditLogEntity columns (camelCase entity properties). jsonb columns
// (diff, metadata), scopeId (PK filter) and traceId are intentionally excluded.
export const AUDIT_LOG_SORT_FIELDS = [
    "id",
    "createdAt",
    "entityId",
    "resourceType",
    "resourceId",
    "changeType",
    "actorId",
    "actorName",
    "actorType",
    "sourceType",
    "sourceId",
    "revertedAt",
] as const;
export const AuditLogSortFieldSchema = z.enum(AUDIT_LOG_SORT_FIELDS);
export type AuditLogSortField = z.infer<typeof AuditLogSortFieldSchema>;

// Explicit <string> element type keeps membership checks cast-free — no
// `as readonly string[]`, which the coding standard forbids.
const AUDIT_LOG_SORT_FIELD_SET = new Set<string>(AUDIT_LOG_SORT_FIELDS);
export function isAuditLogSortField(value: string): value is AuditLogSortField {
    return AUDIT_LOG_SORT_FIELD_SET.has(value);
}

export const AuditLogQuerySchema = z.object({
    filters: AuditLogFilterSchema.optional(),
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).default(20),
    sort: z
        .object({
            field: AuditLogSortFieldSchema,
            order: z.nativeEnum(SortOrder),
        })
        .optional(),
});

export const AuditLogResponseSchema = z.object({
    data: z.array(AuditLogSchema),
    total: z.number(),
    page: z.number(),
    totalPages: z.number(),
});

export const HistoryGroupBySchema = z.nativeEnum(HistoryGroupBy);

export const HistoryResourceSchema = z.record(z.string(), z.unknown());

export const GroupByDiffValueSchema = z.object({
    field: z.string(),
    value: z.unknown(),
});

export const CoaTemplateApplyHistoryEventRefSchema = z
    .object({
        eventId: z.string().uuid(),
        entityId: z.number().int(),
        workspaceId: z.string().uuid(),
    })
    .strict();

export const CoaTemplateApplyHistoryPresentationSchema = z
    .object({
        kind: z.literal("coaTemplateApply"),
        templateName: z.string(),
        eventRefs: z.array(CoaTemplateApplyHistoryEventRefSchema),
    })
    .strict();

export const ScheduleActivityHistoryPresentationSchema =
    scheduleActivityQualifiersSchema
        .partial()
        .extend({ kind: z.literal("scheduleActivity") })
        .strict();

export const HistoryPresentationSchema = z
    .discriminatedUnion("kind", [
        CoaTemplateApplyHistoryPresentationSchema,
        ScheduleActivityHistoryPresentationSchema,
        ReportSettingsUpdateHistoryPresentationSchema,
        ReportTemplateChangeHistoryPresentationSchema,
    ])
    .nullable();

export const HistoryItemSchema = z
    .object({
        date: utcDatetime,
        sourceType: z.string(),
        resourceType: z.nativeEnum(ResourceType),
        actorId: z.number().nullable(),
        organizationActorId: z.string().uuid().nullable().optional(),
        actorName: z.string().nullable(),
        actorType: z.nativeEnum(ActorType).nullable(),
        fields: z.array(z.string()),
        count: z.number(),
        changeTypes: z.array(z.nativeEnum(AuditLogChangeType)),
        resourceIds: z.array(z.string()),
        entityIds: z.array(z.number()),
        sourceIds: z.array(z.string()),
        groupBy: HistoryGroupBySchema,
        groupByDiffValue: GroupByDiffValueSchema.optional(),
        resources: z.array(HistoryResourceSchema).optional(),
        system: z.string().nullable().optional(),
        presentation: HistoryPresentationSchema,
        revertedAt: utcDatetime.nullable().optional(),
        channel: z.nativeEnum(AuditChannel).nullable().optional(),
        workspaceId: z.string().uuid().optional(),
        clientWorkspaceIds: z.array(z.string().uuid()).optional(),
    })
    .strict();

export const RelatedHistoryResourceSchema = z
    .object({
        type: z.literal(ResourceType.CLOSE),
        id: z.string().uuid(),
    })
    .strict();

export const HistoryQuerySchema = z.object({
    filters: HistoryFilterSchema.optional(),
    page: z.coerce.number().default(1),
    limit: z.coerce.number().default(20),
    groupByChannel: z.coerce.boolean().optional(),
    relatedResource: RelatedHistoryResourceSchema.optional(),
});

export const HistoryResponseSchema = z.object({
    data: z.array(HistoryItemSchema),
    total: z.number(),
    page: z.number(),
    totalPages: z.number(),
});

const objectCountSchema = z.number().int().nonnegative();

/** Distinct objects one series changed in a bucket, and per outcome; zero outcomes are left out. */
export const HistoryCountsSeriesSchema = z
    .object({
        total: objectCountSchema,
        outcomes: z.record(
            z.nativeEnum(ActivityOutcomeKind),
            objectCountSchema,
        ),
    })
    .strict();

export const HistoryCountsSeriesBreakdownSchema = z
    .object({
        [ActivitySeries.PEOPLE]: HistoryCountsSeriesSchema,
        [ActivitySeries.KICK]: HistoryCountsSeriesSchema,
        [ActivitySeries.INTEGRATIONS]: HistoryCountsSeriesSchema,
        [ActivitySeries.RULES]: HistoryCountsSeriesSchema,
    })
    .strict();

/** The People series split by person, keyed by user id; people who changed nothing are left out. */
export const HistoryCountsPeopleBreakdownSchema = z.record(
    z.string(),
    HistoryCountsSeriesSchema,
);

export const HistoryDailyCountSchema = z
    .object({
        /** The day, the Monday a week bucket starts on, the 1st of a month bucket, or an hour's local start (`YYYY-MM-DDTHH:00`). */
        date: historyCountsBucketKeySchema,
        count: objectCountSchema,
        series: HistoryCountsSeriesBreakdownSchema.optional(),
        people: HistoryCountsPeopleBreakdownSchema.optional(),
    })
    .strict();

export const HistoryDailyCountsRangeSchema = z
    .object({
        start: isoDateSchema,
        end: isoDateSchema,
    })
    .strict();

export const HistoryDailyCountsQueryObjectSchema = z.object({
    filters: HistoryFilterSchema.optional(),
    bucket: z.nativeEnum(HistoryCountsBucket).optional(),
    /** Buckets and the range follow this zone's midnight; UTC by default or when the zone is unknown. */
    timeZone: ActivityTimeZoneSchema.catch("UTC").optional(),
    breakdown: z.nativeEnum(HistoryCountsBreakdown).optional(),
});

export const HistoryDailyCountsQuerySchema =
    HistoryDailyCountsQueryObjectSchema.superRefine(refineHistoryCountsRange);

export const HistoryDailyCountsResponseSchema = z
    .object({
        data: z.array(HistoryDailyCountSchema),
        /** Day-precise even with `bucket=week` or `month`, so the first bucket's key can precede `start`; clients clip edge buckets. */
        range: HistoryDailyCountsRangeSchema.nullable(),
    })
    .strict();

// Details-drawer and revert discriminator: the clicked row's own channel, or
// "none" for a channel-less row.
const HistoryDetailsChannelSchema = z.union([
    z.nativeEnum(AuditChannel),
    z.literal(NO_HISTORY_CHANNEL),
]);

export const HistoryDetailsQueryObjectSchema = z.object({
    date: z.coerce.date(),
    sourceType: z.string(),
    resourceType: z.nativeEnum(ResourceType).optional(),
    actorId: z.union([z.literal(null), z.coerce.number()]).optional(),
    organizationActorId: zQueryNullableUuid().optional(),
    entityIds: z.array(z.coerce.number()).optional(),
    changeTypes: z.array(z.nativeEnum(AuditLogChangeType)).optional(),
    fields: z.array(z.string()).optional(),
    sourceIds: z.array(z.string()).optional(),
    search: z.string().optional(),
    page: z.coerce.number().default(1),
    limit: z.coerce.number().default(20),
    grouped: z.coerce.boolean().optional(),
    drillDownResourceType: z.nativeEnum(ResourceType).optional(),
    drillDownChangeType: z.nativeEnum(AuditLogChangeType).optional(),
    showReverted: z.coerce.boolean().optional(),
    revertedOnly: z.coerce.boolean().optional(),
    groupBySourceId: z.coerce.boolean().optional(),
    channel: HistoryDetailsChannelSchema.optional(),
    channels: z.array(z.nativeEnum(AuditChannel)).optional(),
    outcome: HistoryFilterObjectSchema.shape.outcome,
    relatedResource: RelatedHistoryResourceSchema.optional(),
});

export const HistoryDetailsQuerySchema = HistoryDetailsQueryObjectSchema.refine(
    (data) => !data.showReverted || !data.revertedOnly,
    {
        message: "showReverted and revertedOnly are mutually exclusive",
    },
);

export const ResourceSchema = z.record(z.string(), z.any());
export const HistoryDetailsItemSchema = AuditLogSchema.extend({
    resource: ResourceSchema.optional(),
});

export const HistoryDetailsResponseSchema = z.object({
    data: z.array(HistoryDetailsItemSchema),
    total: z.number(),
    page: z.number(),
    totalPages: z.number(),
});

export const HistoryDetailsGroupSchema = z.object({
    resourceType: z.nativeEnum(ResourceType),
    changeType: z.nativeEnum(AuditLogChangeType),
    count: z.number(),
    date: utcDatetime,
    resourceIds: z.array(z.string()),
});

export const GroupedHistoryDetailsResponseSchema = z.object({
    groups: z.array(HistoryDetailsGroupSchema),
    details: z.array(HistoryDetailsItemSchema),
    total: z.number(),
});

export const AuditLogRuleConditionSchema = z.object({
    conditionType: z.string(),
    value: z.unknown(),
});

export const SourceIdGroupSchema = z.object({
    sourceId: z.string(),
    count: z.number(),
    latestDate: utcDatetime,
    isReverted: z.boolean(),
    ruleConditions: z.array(AuditLogRuleConditionSchema).optional(),
});

export const SourceIdGroupedResponseSchema = z.object({
    groups: z.array(SourceIdGroupSchema),
    total: z.number(),
});

export const HistoryDetailsErrorSchema = z.object({
    message: z.string(),
});

export const RevertHistoryRequestSchema = z.object({
    date: z.string().datetime({ offset: true }),
    sourceType: z.enum([
        SQSProcessName.TRANSACTION_RULE_APPLICATION,
        APIProcessName.TRANSACTION_BULK_UPDATE,
        APIProcessName.ACCRUAL_REVIEW_APPROVE,
        APIProcessName.ACCRUAL_REVIEW_BULK_APPROVE,
        APIProcessName.ACCRUAL_MATCH_CREATE,
        ActivityProcessName.TRANSACTION_ENRICHMENT,
    ]),
    resourceType: z.nativeEnum(ResourceType),
    actorId: z.number().nullable(),
    fields: z.array(z.string()).min(1),
    changeTypes: z
        .array(z.nativeEnum(AuditLogChangeType))
        .min(1, "At least one change type is required")
        .refine(
            (changeTypes) => !changeTypes.includes(AuditLogChangeType.DOWNLOAD),
            "Download events can't be reverted",
        ),
    sourceId: z.string().optional(),
    resourceIds: z.array(z.string()).min(1).optional(),
    system: z.string().optional(),
    channel: HistoryDetailsChannelSchema.optional(),
    channels: z.array(z.nativeEnum(AuditChannel)).optional(),
    dryRun: z.boolean().optional(),
});

export const RevertHistoryResponseSchema = z.object({
    jobId: z.string(),
});

export const RevertHistoryPreviewFieldSchema = z.object({
    field: z.string(),
    transactionCount: z.number(),
});

export const RevertHistoryPreviewResponseSchema = z.object({
    transactionCount: z.number(),
    fields: z.array(RevertHistoryPreviewFieldSchema),
    alreadyRevertedCount: z.number(),
});

export const auditLogContract = c.router(
    {
        getAuditLogs: {
            method: "GET",
            path: "/:workspaceId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            query: AuditLogQuerySchema,
            responses: {
                200: AuditLogResponseSchema,
            },
        },
        getHistory: {
            method: "GET",
            path: "/:workspaceId/history",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            query: HistoryQuerySchema,
            responses: {
                200: HistoryResponseSchema,
                400: HistoryDetailsErrorSchema,
            },
        },
        getHistoryDailyCounts: {
            method: "GET",
            path: "/:workspaceId/history/daily-counts",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            query: HistoryDailyCountsQuerySchema,
            responses: {
                200: HistoryDailyCountsResponseSchema,
                400: HistoryDetailsErrorSchema,
            },
        },
        getHistoryDetails: {
            method: "GET",
            path: "/:workspaceId/history/details",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            query: HistoryDetailsQuerySchema,
            responses: {
                200: z.union([
                    HistoryDetailsResponseSchema,
                    GroupedHistoryDetailsResponseSchema,
                    SourceIdGroupedResponseSchema,
                ]),
                400: HistoryDetailsErrorSchema,
            },
        },
        revertHistory: {
            method: "POST",
            path: "/:workspaceId/history/revert",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            body: RevertHistoryRequestSchema,
            responses: {
                200: z.union([
                    RevertHistoryResponseSchema,
                    RevertHistoryPreviewResponseSchema,
                ]),
                400: HistoryDetailsErrorSchema,
                404: HistoryDetailsErrorSchema,
            },
        },
    },
    {
        pathPrefix: "/history",
    },
);
